/**
 * Schema Status Header Component
 *
 * Displays parse -> validate pipeline status for the Smart Asset schema.
 * Shows counts of parsed elements and validation errors.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useMemo } from "react";
import type { ValueFlowsSchema } from "../../../types/valueFlows";

interface SchemaStatusHeaderProps {
  /** Value flows schema to analyze */
  schema: ValueFlowsSchema;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Validation result type
 */
interface ValidationResult {
  status: "pass" | "warn" | "error";
  errorCount: number;
  warningCount: number;
  errors: string[];
  warnings: string[];
}

/**
 * Parse counts from schema
 */
interface ParseCounts {
  elements: number;
  flows: number;
  observables: number;
  expressions: number;
  bindings: number;
}

/**
 * Count parsed elements
 */
function countParsedElements(schema: ValueFlowsSchema): ParseCounts {
  const elements =
    Object.keys(schema.external_services || {}).length +
    Object.keys(schema.internal_elements || {}).length +
    Object.keys(schema.boundary_gates || {}).length +
    Object.keys(schema.sources || {}).length +
    Object.keys(schema.sinks || {}).length;

  const flows = Object.keys(schema.flows || {}).length;
  const observables = Object.keys(schema.observables || {}).length;
  const expressions = Object.keys(schema.contract_algebra?.expressions || {}).length;

  // Count total bindings across all categories
  let bindings = 0;
  if (schema.contract_algebra?.bindings) {
    for (const categoryBindings of Object.values(schema.contract_algebra.bindings)) {
      bindings += Object.keys(categoryBindings).length;
    }
  }

  return { elements, flows, observables, expressions, bindings };
}

/**
 * Validate schema and collect errors
 */
function validateSchema(schema: ValueFlowsSchema): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for missing boundary
  if (!schema.boundary) {
    errors.push("Missing boundary definition");
  }

  // Check for broken references in flows
  if (schema.flows) {
    for (const [flowId, flow] of Object.entries(schema.flows)) {
      if (flow.source && !resolveElement(schema, flow.source)) {
        errors.push(`Flow "${flowId}": source "${flow.source}" not found`);
      }
      if (flow.target && !resolveElement(schema, flow.target)) {
        errors.push(`Flow "${flowId}": target "${flow.target}" not found`);
      }
    }
  }

  // Check for missing observable references
  if (schema.observables) {
    for (const [obsId, obs] of Object.entries(schema.observables)) {
      if (obs.source_ref) {
        const parts = obs.source_ref.split(".");
        if (parts.length >= 2) {
          const category = parts[0];
          const elementId = parts.slice(1).join(".");
          const categoryMap = getCategoryMap(schema, category);
          if (categoryMap && !categoryMap[elementId]) {
            warnings.push(`Observable "${obsId}": source_ref "${obs.source_ref}" not found`);
          }
        }
      }
      if (obs.gate_binding) {
        if (!schema.boundary_gates?.[obs.gate_binding.gate_id]) {
          errors.push(`Observable "${obsId}": gate "${obs.gate_binding.gate_id}" not found`);
        }
      }
    }
  }

  // Check for expression references
  if (schema.contract_algebra?.expressions) {
    for (const [exprId, expr] of Object.entries(schema.contract_algebra.expressions)) {
      if (expr.arg && !schema.contract_algebra.expressions[expr.arg]) {
        warnings.push(`Expression "${exprId}": arg "${expr.arg}" not defined`);
      }
      if (expr.args) {
        for (const argRef of expr.args) {
          if (!schema.contract_algebra.expressions[argRef]) {
            warnings.push(`Expression "${exprId}": arg "${argRef}" not defined`);
          }
        }
      }
    }
  }

  const errorCount = errors.length;
  const warningCount = warnings.length;
  const status = errorCount > 0 ? "error" : warningCount > 0 ? "warn" : "pass";

  return { status, errorCount, warningCount, errors, warnings };
}

/**
 * Get category map from schema
 */
function getCategoryMap(
  schema: ValueFlowsSchema,
  category: string,
): Record<string, unknown> | undefined {
  switch (category) {
    case "internal_elements":
      return schema.internal_elements;
    case "external_services":
      return schema.external_services;
    case "boundary_gates":
      return schema.boundary_gates;
    case "sources":
      return schema.sources;
    case "sinks":
      return schema.sinks;
    case "flows":
      return schema.flows;
    default:
      return undefined;
  }
}

/**
 * Resolve element ID to check if it exists
 */
function resolveElement(schema: ValueFlowsSchema, id: string): boolean {
  // Check all categories
  if (schema.external_services?.[id]) return true;
  if (schema.internal_elements?.[id]) return true;
  if (schema.boundary_gates?.[id]) return true;
  if (schema.sources?.[id]) return true;
  if (schema.sinks?.[id]) return true;
  return false;
}

/**
 * Check icon SVG
 */
const CheckIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Warning icon SVG
 */
const WarningIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

/**
 * Error icon SVG
 */
const ErrorIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6m0-6l6 6" />
  </svg>
);

/**
 * Schema Status Header
 *
 * Shows parse counts and validation status for the schema.
 */
export const SchemaStatusHeader = memo(
  ({ schema, compact = false, className = "" }: SchemaStatusHeaderProps) => {
    const counts = useMemo(() => countParsedElements(schema), [schema]);
    const validation = useMemo(() => validateSchema(schema), [schema]);

    const statusColors = {
      pass: "text-sa-success bg-sa-success/10",
      warn: "text-sa-warning bg-sa-warning/10",
      error: "text-sa-danger bg-sa-danger/10",
    };

    const StatusIcon =
      validation.status === "pass"
        ? CheckIcon
        : validation.status === "warn"
          ? WarningIcon
          : ErrorIcon;

    if (compact) {
      // Compact: just show status icon and counts
      return (
        <div className={`flex items-center gap-2 px-2 py-1 ${className}`}>
          <div className={`p-1 rounded ${statusColors[validation.status]}`}>
            <StatusIcon />
          </div>
          <span className="text-xs text-sa-text-dim">
            {counts.elements}e · {counts.flows}f · {counts.observables}o · {counts.expressions}x
          </span>
        </div>
      );
    }

    return (
      <div className={`border-b border-sa-border ${className}`}>
        {/* Status bar */}
        <div className="flex items-center gap-3 px-3 py-2">
          {/* Validation status */}
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded ${statusColors[validation.status]}`}
          >
            <StatusIcon />
            <span className="text-xs font-medium">
              {validation.status === "pass"
                ? "Valid"
                : validation.status === "warn"
                  ? `${validation.warningCount} warning${validation.warningCount !== 1 ? "s" : ""}`
                  : `${validation.errorCount} error${validation.errorCount !== 1 ? "s" : ""}`}
            </span>
          </div>

          {/* Parse counts */}
          <div className="flex items-center gap-3 text-xs text-sa-text-muted">
            <span>
              <span className="text-sa-text">{counts.elements}</span> elements
            </span>
            <span>
              <span className="text-sa-text">{counts.flows}</span> flows
            </span>
            <span>
              <span className="text-sa-text">{counts.observables}</span> obs
            </span>
            <span>
              <span className="text-sa-text">{counts.expressions}</span> expr
            </span>
            {counts.bindings > 0 && (
              <span>
                <span className="text-sa-text">{counts.bindings}</span> bind
              </span>
            )}
          </div>
        </div>

        {/* Error/warning list (if any) */}
        {(validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div className="px-3 py-2 bg-sa-surface/50 text-xs max-h-24 overflow-y-auto">
            {validation.errors.slice(0, 3).map((err) => (
              <div key={`err-${err}`} className="text-sa-danger truncate" title={err}>
                {err}
              </div>
            ))}
            {validation.warnings.slice(0, 3).map((warn) => (
              <div key={`warn-${warn}`} className="text-sa-warning truncate" title={warn}>
                {warn}
              </div>
            ))}
            {validation.errors.length + validation.warnings.length > 6 && (
              <div className="text-sa-text-muted">
                +{validation.errors.length + validation.warnings.length - 6} more...
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

SchemaStatusHeader.displayName = "SchemaStatusHeader";
